<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getFullName } from '@/utils/formatters'
import { useAuditRegisterStore } from '@/modules/audits/stores/useAuditRegisterStore/useAuditRegisterStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import { ERouteNames } from '@/router/ERouteNames'

import AuditCheckProcessCard from '@/modules/audits/stores/useAuditRegisterStore/components/AuditCheckProcessCard.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { AUDITS_ANCHORS, AUDITS_CONTEXTS } from '@/modules/onboarding/scenarios/audits/anchors'

import type {
  IAuditProcessParentGroup,
  IAuditProcessPoint,
} from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditProcessItem'

const auditRegisterStore = useAuditRegisterStore()
const userStore = useUserStore()
const route = useRoute()
const router = useRouter()

const { processingAuditItems } = storeToRefs(auditRegisterStore)

// Онбординг открывает страницу из карточки аудита — назад возвращаемся туда же
useOnboardingContext({
  names: [AUDITS_CONTEXTS.process],
  root: () => document.body,
  actions: {
    backToCard: () => { router.back() },
  },
})

const departmentTab = ref(0)
const criterionTab = ref(0)

const currentProcessItem = computed(() => {
  const selectedDeptId = String(route.params.departmentId)

  if (!processingAuditItems.value) return null

  // Если отделение выбрано — ищем его
  if (selectedDeptId) {
    return processingAuditItems.value.find(item => String(item.department.id) === String(selectedDeptId))
  }

  return processingAuditItems.value[0]
})

const handleChildSaved = async () => {
  const id = Number(route.params.id)
  const departmentId = String(route.params.departmentId)

  // Читаем query параметр из URL
  const parentIdFromUrl = route.query.parent ? String(route.query.parent) : undefined

  await auditRegisterStore.processAudit(
    id,
    departmentId,
    resolveAssignmentId(),
    parentIdFromUrl,
  )
}

const isCriterionFinished = computed(() => {
  if (!currentProcessItem.value) return {}

  const map: Record<string, boolean> = {}

  currentProcessItem.value.parents.forEach(group => {
    map[group.parent.id] = group.points.every(p => p.status !== null)
  })

  return map
})

const pointStatusView: Record<Exclude<IAuditProcessPoint['status'], null>, {
  icon: string
  color: string
}> = {
  complies: { icon: 'ri-checkbox-circle-line', color: 'success' },
  does_not_comply: { icon: 'ri-close-circle-line', color: 'error' },
  not_applicable: { icon: 'ri-indeterminate-circle-line', color: 'secondary' },
}

/* Завершённый предмет проверки неизменяем — кнопку завершения больше не показываем */
const isCriterionCompleted = computed(() => {
  if (!currentProcessItem.value) return {}

  const map: Record<string, boolean> = {}

  currentProcessItem.value.parents.forEach(group => {
    map[group.parent.id] = group.points.length > 0
      && group.points.every(p => p.is_completed)
  })

  return map
})

const completeCriterion = async (group: IAuditProcessParentGroup) => {
  const ids = group.points.map(point => point.id)

  await auditRegisterStore.completeAuditReports(ids)
  await handleChildSaved()

  // Если все критерии отделения завершены — возвращаемся в карточку аудита
  const allCompleted = Boolean(
    currentProcessItem.value?.parents.length
    && currentProcessItem.value.parents.every(g =>
      g.points.length > 0 && g.points.every(p => p.is_completed),
    ),
  )

  if (allCompleted) {
    await router.replace({
      name: ERouteNames.AUDIT_DETAIL,
      params: { id: route.params.id },
    })
  }
}

const activeAssignmentId = computed(() => {
  // 1. Приоритет отдаем URL (если менеджер зашел за коллегу)
  if (route.query.assignment) {
    return Number(route.query.assignment)
  }

  // 2. Иначе берем назначение текущего авторизованного пользователя
  const userId = userStore.user?.id
  return auditRegisterStore.currentAudit?.audit_assignments?.find(
    a => a.employee?.id === userId,
  )?.id
})

const resolveAssignmentId = (): number => {
  const raw = activeAssignmentId.value
  if (raw == null) return NaN
  const n = Number(raw)
  return Number.isFinite(n) ? n : NaN
}

const getCriterionNumber = (label: string) => {
  if (!label) return ''
  // Берем первое слово (код) до пробела
  return label.split(' ')[0]
}

// Сбрасываем вкладку критерия при смене отделения
watch(departmentTab, () => {
  criterionTab.value = 0
})

const openedPanelsByCriterion = computed(() => {
  if (!currentProcessItem.value) return {}

  const map: Record<string, string[]> = {}

  currentProcessItem.value.parents.forEach(group => {
    map[String(group.parent.id)] = group.points
      .filter(p => p.status === null)
      .map(p => String(p.point.id))
  })

  return map
})

onMounted(async () => {
  const id = Number(route.params.id)
  const departmentId = String(route.params.departmentId)
  const parentIdFromUrl = route.query.parent ? String(route.query.parent) : undefined

  if (!id || !departmentId) return

  if (!auditRegisterStore.currentAudit?.id) {
    await auditRegisterStore.fetchAuditById(id)
  }

  if (!processingAuditItems.value.length) {
    await auditRegisterStore.processAudit(
      id,
      departmentId,
      resolveAssignmentId(),
      parentIdFromUrl,
    )
  }
})
</script>

<template>
  <div v-if="currentProcessItem" class="d-flex flex-column gap-2 pb-8">
    <div class="text-h6">
      Проверка отделения: {{ currentProcessItem.department.short_name || currentProcessItem.department.name }}
    </div>

    <div class="d-flex">
      <div v-if="currentProcessItem.parents.length" class="d-flex flex-column gap-2 w-100">
        <VTabs v-model="criterionTab" :data-onboarding="AUDITS_ANCHORS.process.criteria">
          <VTab
            v-for="(critGroup, critIndex) in currentProcessItem.parents"
            :key="critGroup.parent.id"
            :value="critIndex"
            class="d-flex align-center audit-process-tab"
          >
            <VIcon
              v-if="isCriterionCompleted[critGroup.parent.id]"
              icon="ri-check-line"
              color="primary"
              size="16"
            />

            <VTooltip location="end">
              <template #activator="{ props: tabTooltipProps }">
                <span v-bind="tabTooltipProps">{{ getCriterionNumber(critGroup.parent.label) }}</span>
              </template>
              {{ critGroup.parent.label }}
            </VTooltip>
          </VTab>
        </VTabs>

        <VWindow
          v-model="criterionTab"
          :transition="false"
          :touch="false"
          class="flex-grow-1 audit-process-window"
        >
          <VWindowItem
            v-for="(critGroup, critIndex) in currentProcessItem.parents"
            :key="critGroup.parent.id"
            :value="critIndex"
          >
            <div class="d-flex flex-column gap-4">
              <div class="text-h6">
                {{ critGroup.parent.label }}
              </div>
              <VExpansionPanels
                multiple
                class="d-flex flex-column gap-2"
                :data-onboarding="AUDITS_ANCHORS.process.questions"
                :model-value="openedPanelsByCriterion[critGroup.parent.id]"
              >
                <VExpansionPanel
                  v-for="point in critGroup.points"
                  :key="point.point.id"
                  :value="point.point.id"
                  class="audit-question-panel"
                >
                  <VExpansionPanelTitle class="d-flex align-center">
                    <!-- Левая часть - номер вопроса и иконка -->
                    <div class="d-flex align-center gap-2 flex-grow-1">
                      <span>Вопрос {{ point.point.number }}</span>

                      <VIcon
                        v-if="point.status !== null"
                        :icon="pointStatusView[point.status].icon"
                        :color="pointStatusView[point.status].color"
                        size="18"
                      />
                    </div>

                    <!-- Правая часть - информация о проверяющем -->
                    <div
                      v-if="point.status !== null && point.created_by?.id !== userStore.user?.id"
                      class="d-flex align-center gap-1 text-caption text-disabled mr-2"
                    >
                      <VIcon icon="ri-user-3-line" size="14" />
                      <span>Проверил: {{ getFullName(point.created_by ?? {}) }}</span>
                    </div>
                  </VExpansionPanelTitle>

                  <VExpansionPanelText>
                    <AuditCheckProcessCard
                      :assignment_id="resolveAssignmentId()"
                      :parent_id="critGroup.parent.id"
                      :department_id="String(currentProcessItem.department.id)"
                      :point_number="point.point.number"
                      :point_question="point.point.name"
                      :entry="point"
                      @saved="handleChildSaved"
                    />
                  </VExpansionPanelText>
                </VExpansionPanel>
              </VExpansionPanels>
              <VBtn
                v-if="isCriterionFinished[critGroup.parent.id] && !isCriterionCompleted[critGroup.parent.id]"
                variant="elevated"
                color="primary"
                class="align-self-end"
                @click="completeCriterion(critGroup)"
              >
                Завершить проверку
              </VBtn>
            </div>
          </VWindowItem>
        </VWindow>
      </div>

      <div v-else class="text-disabled">
        Нет критериев для проверки в этом отделении
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Поля наблюдения читаются плохо, когда растянуты на всю ширину экрана */
.audit-process-window {
  max-inline-size: 700px;
}

.audit-process-tab :deep(.v-btn__content) {
  gap: 6px;
}

/* Обводка всей панели вопроса (заголовок + контент), когда внутри фокус.
   inset — иначе overflow:hidden у VExpansionPanel обрезает внешний shadow по бокам */
.audit-question-panel {
  transition: box-shadow 0.15s ease;

  &:focus-within {
    box-shadow: inset 0 0 0 2px rgb(var(--v-theme-primary));
  }
}
</style>
